import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import {
  AlertCircle,
  BarChart3,
  BookOpen,
  Check,
  ChevronRight,
  Clipboard,
  Eye,
  EyeOff,
  KeyRound,
  LogOut,
  LockKeyhole,
  UserRound,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  X,
} from 'lucide-react'
import { ApiError, apiFetch } from '../../api/client'
import { DataUpdatesPanel } from './DataUpdatesPanel'
import './admin.css'

type ApiKey = {
  id: string
  prefix: string
  name: string
  created_at: string
  last_used_at: string | null
  expires_at: string | null
  revoked_at: string | null
  can_reveal: boolean
}

type CreatedApiKey = ApiKey & {
  key: string
}

type AdminSession = { username: string; csrf_token: string }

type Status = 'active' | 'expired' | 'revoked'

const STATUS_LABELS: Record<Status, string> = { active: '有效', expired: '已过期', revoked: '已撤销' }

// Only display the server's controlled diagnostics; older or unexpected errors
// retain the general explanation instead of exposing arbitrary server details.
const ENCRYPTION_DIAGNOSTICS = new Set([
  'API 密钥加密文件无权读取，请检查挂载目录和文件的读取权限。',
  'API 密钥加密目录无权写入，请检查持久化挂载目录的所有者和写入权限。',
  'API 密钥加密文件不存在，请恢复原加密文件备份；已有密文不能使用新文件解密。',
  'API 密钥加密文件为空，请恢复原加密文件备份。',
  'API 密钥加密文件格式无效，请检查文件或恢复原加密文件备份。',
  'API 密钥加密密文无法使用当前文件解密，可能是文件不匹配或密文损坏，请检查并恢复对应备份。',
  'API 密钥加密文件无法原子保存，请检查挂载存储是否支持硬链接以及目录权限。',
  'API 密钥加密存储无法访问，请检查挂载路径、存储空间和文件系统状态。',
])

function keyStatus(key: ApiKey): Status {
  if (key.revoked_at) return 'revoked'
  return key.expires_at && new Date(key.expires_at).getTime() <= Date.now() ? 'expired' : 'active'
}

function formatDate(value: string | null) {
  if (!value) return '从未使用'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat('zh-CN', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(date)
}

function errorMessage(error: unknown) {
  if (error instanceof ApiError) {
    if (error.code === 'ADMIN_INVALID_CREDENTIALS') return '账号或密码不正确，请重新输入。'
    if (error.status === 401) return '登录已过期，请重新登录。'
    if (error.code === 'ADMIN_CSRF_INVALID') return '会话验证未通过，请退出后重新登录。'
    if (error.code === 'ADMIN_ORIGIN_INVALID') return '当前访问地址未通过验证，请从本站登录后重试。'
    if (error.status === 429) return '操作过于频繁，请稍后重试。'
    if (error.code === 'INVALID_ARGUMENT') return error.message
    if (error.status === 403) return '当前操作未通过权限验证，请重新登录。'
    if (error.code === 'API_KEY_SECRET_UNAVAILABLE') return '历史密钥未保存可恢复的完整值，请创建新密钥并替换旧凭证。'
    if (error.code === 'API_KEY_ENCRYPTION_UNAVAILABLE') {
      return ENCRYPTION_DIAGNOSTICS.has(error.message)
        ? error.message
        : '服务器的密钥加密配置不可用，请检查或恢复服务器上的密钥加密文件后重试。'
    }
    if (error.status === 404) return '密钥不存在，请关闭弹窗并刷新列表。'
    if (error.status === 0) return '无法连接 API 服务，请检查网络或服务状态。'
    return `API 请求失败（HTTP ${error.status}）：${error.message}`
  }
  return error instanceof Error ? error.message : '发生未知错误，请稍后重试。'
}

function ErrorNotice({ message }: {
  message: string
}) {
  return (
    message ? <div className="admin-error" role="alert">
      <AlertCircle size={17} />
      <span>
        {message}
      </span>
    </div> : null
  )
}

function KeyState({ apiKey }: {
  apiKey: ApiKey
}) {
  const status = keyStatus(apiKey)
  return (
    <span className={`admin-status admin-status-${status}`}>
      <i />
      {STATUS_LABELS[status]}
    </span>
  )
}

function Modal({ title, description, onClose, busy = false, danger = false, children }: {
  title: string
  description: string
  onClose: () => void
  busy?: boolean
  danger?: boolean
  children: ReactNode
}) {
  const ref = useRef<HTMLElement>(null)
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null
    const panel = ref.current
    const target = panel?.querySelector<HTMLElement>('input:not(:disabled), textarea:not(:disabled), select:not(:disabled)')
      ?? panel?.querySelector<HTMLElement>('button:not(:disabled)')
      ?? panel
    target?.focus()
    return () => {
      if (previous?.isConnected) previous.focus()
    }
  }, [])
  return (
    <div className="admin-modal-backdrop" onMouseDown={(event) => {
      if (event.target === event.currentTarget && !busy) onClose()
    }}>
      <section
        ref={ref}
        className="admin-dialog"
        tabIndex={-1}
        role={danger ? 'alertdialog' : 'dialog'}
        aria-modal="true"
        aria-labelledby="admin-dialog-title"
        aria-describedby="admin-dialog-description"
        onKeyDown={(event) => {
          if (event.key === 'Escape' && !busy) {
            event.preventDefault()
            onClose()
          }
          if (event.key !== 'Tab') return
          const items = Array.from(ref.current?.querySelectorAll<HTMLElement>(
            'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]',
          ) ?? [])
          const first = items[0]
          const last = items[items.length - 1]
          if (!items.length) {
            event.preventDefault()
            ref.current?.focus()
          } else if (document.activeElement === ref.current) {
            event.preventDefault()
            if (event.shiftKey) last?.focus()
            else first?.focus()
          } else if (event.shiftKey && document.activeElement === first) {
            event.preventDefault()
            last?.focus()
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault()
            first?.focus()
          }
        }}
      >
        <div className="admin-dialog-heading">
          <div>
            <h2 id="admin-dialog-title">
              {title}
            </h2>
            <p id="admin-dialog-description">
              {description}
            </p>
          </div>
          <button className="admin-icon-button" type="button" aria-label="关闭弹窗" disabled={busy} onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        {children}
      </section>
    </div>
  )
}

export function AdminPage() {
  const [activePanel, setActivePanel] = useState<'keys' | 'updates'>('keys')
  const [username, setUsername] = useState('admin')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [session, setSession] = useState<AdminSession | null>(null)
  const [checkingSession, setCheckingSession] = useState(true)
  const [signingIn, setSigningIn] = useState(false)
  const [signingOut, setSigningOut] = useState(false)
  const [notice, setNotice] = useState('')
  const [showChangePassword, setShowChangePassword] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [visiblePasswords, setVisiblePasswords] = useState<Record<string, boolean>>({})
  const [changingPassword, setChangingPassword] = useState(false)
  const [keys, setKeys] = useState<ApiKey[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState('all')
  const [showCreate, setShowCreate] = useState(false)
  const [name, setName] = useState('')
  const [expiryMode, setExpiryMode] = useState('never')
  const [expiresAt, setExpiresAt] = useState('')
  const [creating, setCreating] = useState(false)
  const [detail, setDetail] = useState<ApiKey | null>(null)
  const [secret, setSecret] = useState('')
  const [revealing, setRevealing] = useState<string | null>(null)
  const [copyFeedback, setCopyFeedback] = useState('')
  const [manualCopy, setManualCopy] = useState(false)
  const [modalError, setModalError] = useState('')
  const [revokeTarget, setRevokeTarget] = useState<ApiKey | null>(null)
  const [revoking, setRevoking] = useState(false)
  // 明文仅保留在当前页面会话中，结束会话和卸载时清除。
  const secrets = useRef(new Map<string, string>())
  // 避免旧请求在退出后覆盖新会话。
  const epoch = useRef(0)
  const secretInput = useRef<HTMLTextAreaElement>(null)
  useEffect(() => {
    if (manualCopy) {
      secretInput.current?.focus()
      secretInput.current?.select()
    }
  }, [manualCopy, secret])
  useEffect(() => {
    if (copyFeedback !== '密钥已复制') return
    const timer = window.setTimeout(() => setCopyFeedback(''), 2200)
    return () => window.clearTimeout(timer)
  }, [copyFeedback])
  useEffect(() => () => {
    epoch.current += 1
    secrets.current.clear()
  }, [])
  async function adminRequest<T>(path: string, options: RequestInit = {}, currentSession = session): Promise<T> {
    const requestEpoch = epoch.current
    const mutation = options.method && options.method !== 'GET'
    try {
      return await apiFetch<T>(path, {
        ...options,
        headers: { ...options.headers, ...(mutation && currentSession ? { 'X-CSRF-Token': currentSession.csrf_token } : {}) },
      })
    } catch (requestError) {
      if (requestError instanceof ApiError && requestError.status === 401 && requestError.code === 'ADMIN_UNAUTHORIZED' && requestEpoch === epoch.current) {
        clearSession('登录已过期，请重新登录。')
      }
      throw requestError
    }
  }
  async function loadKeys(currentSession = session): Promise<boolean> {
    if (!currentSession) return false
    const requestEpoch = epoch.current
    setLoading(true)
    setError('')
    try {
      const result = await adminRequest<{ keys: ApiKey[] }>('/admin/keys', {}, currentSession)
      if (!Array.isArray(result.keys)) throw new Error('API 响应格式错误：密钥列表缺失。')
      if (requestEpoch !== epoch.current) return false
      setKeys(result.keys)
      return true
    } catch (requestError) {
      if (requestEpoch === epoch.current) setError(errorMessage(requestError))
      return false
    } finally {
      if (requestEpoch === epoch.current) setLoading(false)
    }
  }
  useEffect(() => {
    let cancelled = false
    const requestEpoch = epoch.current
    async function restoreSession() {
      try {
        const restored = await apiFetch<AdminSession>('/admin/auth/session')
        if (cancelled || requestEpoch !== epoch.current) return
        setSession(restored)
        setUsername(restored.username)
        await loadKeys(restored)
      } catch (requestError) {
        if (!cancelled && requestEpoch === epoch.current && !(requestError instanceof ApiError && requestError.status === 401)) {
          setError(errorMessage(requestError))
        }
      } finally {
        if (!cancelled && requestEpoch === epoch.current) setCheckingSession(false)
      }
    }
    void restoreSession()
    return () => { cancelled = true }
    // Session restoration runs once; every subsequent request uses the current session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  async function connect(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!username.trim() || !password) {
      setError('请输入账号和密码。')
      return
    }
    const requestEpoch = epoch.current
    setSigningIn(true)
    setError('')
    setNotice('')
    try {
      const result = await apiFetch<AdminSession>('/admin/auth/login', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim(), password }),
      })
      if (requestEpoch !== epoch.current) return
      setSession(result)
      setUsername(result.username)
      setPassword('')
      setShowPassword(false)
      setNotice('登录成功。')
      await loadKeys(result)
    } catch (requestError) {
      if (requestEpoch === epoch.current) setError(errorMessage(requestError))
    } finally {
      if (requestEpoch === epoch.current) setSigningIn(false)
    }
  }
  async function logout() {
    const requestEpoch = epoch.current
    setSigningOut(true)
    setError('')
    try {
      await adminRequest('/admin/auth/logout', { method: 'POST' })
      if (requestEpoch === epoch.current) clearSession('已退出登录。')
    } catch (requestError) {
      if (requestEpoch === epoch.current) setError(errorMessage(requestError))
    } finally {
      if (requestEpoch === epoch.current) setSigningOut(false)
    }
  }
  async function changePassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setModalError('')
    if (newPassword.length < 8 || newPassword.length > 128) {
      setModalError('新密码需要 8–128 个字符。')
      return
    }
    if (newPassword !== confirmPassword) {
      setModalError('两次输入的新密码不一致。')
      return
    }
    const requestEpoch = epoch.current
    setChangingPassword(true)
    try {
      await adminRequest('/admin/auth/password', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
      })
      if (requestEpoch === epoch.current) clearSession('密码已修改，请使用新密码重新登录。')
    } catch (requestError) {
      if (requestEpoch === epoch.current) setModalError(errorMessage(requestError))
    } finally {
      if (requestEpoch === epoch.current) setChangingPassword(false)
    }
  }
  function startChangePassword() {
    closeModal()
    setShowChangePassword(true)
  }
  function closeModal() {
    setShowChangePassword(false)
    setCurrentPassword('')
    setNewPassword('')
    setConfirmPassword('')
    setVisiblePasswords({})
    setShowCreate(false)
    setDetail(null)
    setRevokeTarget(null)
    setSecret('')
    setModalError('')
    setCopyFeedback('')
    setManualCopy(false)
  }
  function startCreate() {
    closeModal()
    setName('')
    setExpiryMode('never')
    setExpiresAt('')
    setShowCreate(true)
  }
  function clearSession(message = '') {
    epoch.current += 1
    secrets.current.clear()
    closeModal()
    setSession(null)
    setPassword('')
    setKeys([])
    setActivePanel('keys')
    setSearch('')
    setFilter('all')
    setError('')
    setNotice(message)
    setLoading(false)
    setCheckingSession(false)
    setSigningIn(false)
    setSigningOut(false)
    setChangingPassword(false)
    setRevealing(null)
    setShowPassword(false)
    setCreating(false)
    setRevoking(false)
  }
  async function createKey(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!session || !name.trim()) return
    let expires: string | null = null
    if (expiryMode === 'custom') {
      const date = new Date(expiresAt)
      if (!expiresAt || Number.isNaN(date.getTime()) || date.getTime() <= Date.now()) {
        setModalError('请选择未来的过期时间。')
        return
      }
      expires = date.toISOString()
    }
    const requestEpoch = epoch.current
    setCreating(true)
    setModalError('')
    try {
      const created = await adminRequest<CreatedApiKey>('/admin/keys', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), expires_at: expires })
      })
      if (!created.key || !created.id) throw new Error('API 响应格式错误：未返回新密钥。')
      if (requestEpoch !== epoch.current) return
      secrets.current.set(created.id, created.key)
      const metadata = {
        id: created.id,
        prefix: created.prefix,
        name: created.name,
        created_at: created.created_at,
        expires_at: created.expires_at,
        revoked_at: created.revoked_at,
        last_used_at: created.last_used_at ?? null,
        can_reveal: true
      }
      setKeys((current) => [metadata, ...current])
      setShowCreate(false)
      setDetail(metadata)
      setSecret(created.key)
      setName('')
      await loadKeys()
    } catch (requestError) {
      if (requestEpoch === epoch.current) setModalError(errorMessage(requestError))
    }
    finally {
      if (requestEpoch === epoch.current) setCreating(false)
    }
  }
  async function reveal(apiKey: ApiKey, copy = false) {
    if (!session || !apiKey.can_reveal || revealing) return
    closeModal()
    setDetail(apiKey)
    const requestEpoch = epoch.current
    setRevealing(apiKey.id)
    try {
      let value = secrets.current.get(apiKey.id)
      if (!value) {
        const result = await adminRequest<{
          id: string
          key: string
        }>(`/admin/keys/${encodeURIComponent(apiKey.id)}/secret`)
        if (result.id !== apiKey.id || !result.key) throw new Error('API 响应格式错误：未返回完整密钥。')
        value = result.key
      }
      if (requestEpoch !== epoch.current) return
      secrets.current.set(apiKey.id, value)
      setSecret(value)
      if (copy) await copyKey(value, requestEpoch)
    } catch (requestError) {
      if (requestEpoch === epoch.current) setModalError(errorMessage(requestError))
    }
    finally {
      if (requestEpoch === epoch.current) setRevealing(null)
    }
  }
  async function copyKey(value = secret, requestEpoch = epoch.current) {
    if (!value) return
    try {
      if (!navigator.clipboard?.writeText) throw new Error('clipboard unavailable')
      await navigator.clipboard.writeText(value)
      if (requestEpoch === epoch.current) {
        setManualCopy(false)
        setCopyFeedback('密钥已复制')
      }
    } catch {
      if (requestEpoch === epoch.current) {
        setManualCopy(true)
        setCopyFeedback('浏览器未允许自动复制，已选中密钥，请按 Ctrl+C 或长按复制。')
        secretInput.current?.focus()
        secretInput.current?.select()
      }
    }
  }
  async function revokeKey() {
    if (!session || !revokeTarget) return
    const requestEpoch = epoch.current
    setRevoking(true)
    setModalError('')
    try {
      const result = await adminRequest<{
        id: string
        revoked: boolean
      }>(`/admin/keys/${encodeURIComponent(revokeTarget.id)}`, { method: 'DELETE' })
      if (!result.revoked || result.id !== revokeTarget.id) throw new Error('API 响应未确认密钥已撤销。')
      if (requestEpoch !== epoch.current) return
      setKeys((current) => current.map((key) => key.id === result.id ? { ...key, revoked_at: new Date().toISOString() } : key))
      closeModal()
      await loadKeys()
    } catch (requestError) {
      if (requestEpoch === epoch.current) setModalError(errorMessage(requestError))
    }
    finally {
      if (requestEpoch === epoch.current) setRevoking(false)
    }
  }
  const visibleKeys = keys.filter((key) => (filter === 'all' || keyStatus(key) === filter) && `${key.name} ${key.prefix}`.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()))
  return (
    <main className="admin-shell">
      <header className="admin-topbar">
        <Link className="admin-brand" to="/stocks" aria-label="返回股票目录">
          <span className="admin-brand-mark">
            <BarChart3 size={21} />
          </span>
          <span>
            <strong>个股查询</strong>
            <small>股票数据服务</small>
          </span>
        </Link>
        <nav className="admin-topbar-links" aria-label="管理导航">
          <Link className="admin-back-link" to="/stocks" aria-label="股票查询">
            <BarChart3 size={16} />
            <span className="admin-nav-label">股票查询</span>
          </Link>
          <Link className="admin-back-link" to="/api-docs" target="_blank" rel="noopener noreferrer" aria-label="API 文档">
            <BookOpen size={16} />
            <span className="admin-nav-label">API 文档</span>
          </Link>
          <Link className="admin-back-link admin-nav-current" to="/admin" aria-current="page" aria-label="管理后台">
            <ShieldCheck size={16} />
            <span className="admin-nav-label">管理后台</span>
          </Link>
        </nav>
      </header>
      <div className="admin-content">
        <div className="admin-breadcrumb">
          管理控制台
          <ChevronRight size={13} />
          <span>{session ? activePanel === 'keys' ? '访问凭证' : '数据更新' : '管理员登录'}</span>
        </div>
        <div className="admin-heading-row">
          <div>
            <h1>{session ? activePanel === 'keys' ? 'API 密钥' : '数据更新' : '管理控制台'}</h1>
            <p className="admin-subtitle">{session ? activePanel === 'keys' ? '管理访问凭证，让数据连接更简单。' : '了解每次更新的结果，按需要调整更新周期。' : '登录账户，管理你的股票数据服务。'}</p>
          </div>
          {session && <div className="admin-session">
            <span>
              <i />
              {session.username}
            </span>
            <button className="admin-button admin-button-quiet" type="button" onClick={startChangePassword}><LockKeyhole size={16} />修改密码</button>
            <button className="admin-button admin-button-quiet" type="button" onClick={() => void logout()} disabled={signingOut}>
              <LogOut size={16} />
              {signingOut ? '退出中' : '退出登录'}
            </button>
          </div>}
        </div>
        {session && <nav className="admin-sections" aria-label="后台功能">
          <button className={activePanel === 'keys' ? 'is-current' : ''} type="button" aria-pressed={activePanel === 'keys'} onClick={() => setActivePanel('keys')}><KeyRound size={17} />API 密钥</button>
          <button className={activePanel === 'updates' ? 'is-current' : ''} type="button" aria-pressed={activePanel === 'updates'} onClick={() => setActivePanel('updates')}><RefreshCw size={17} />数据更新</button>
        </nav>}
        {error && <div className="admin-error-row">
          <ErrorNotice message={error} />
          {session && <button className="admin-button" type="button" disabled={loading} onClick={() => void loadKeys()}>重试</button>}
        </div>}
        {notice && <p className="admin-notice" role="status"><Check size={16} />{notice}</p>}
        {checkingSession ? <section className="admin-auth-panel admin-checking" aria-label="检查登录状态" aria-busy="true"><RefreshCw className="admin-spin" size={24} /><p>正在检查登录状态…</p></section> : !session ? <section className="admin-auth-panel" aria-labelledby="admin-auth-title">
          <div className="admin-auth-symbol"><LockKeyhole size={26} /></div>
          <h2 id="admin-auth-title">欢迎回来</h2>
          <p>使用管理员账号登录控制台。</p>
          <form onSubmit={connect}>
            <label className="admin-field" htmlFor="admin-username">
              <span className="admin-field-label">管理员账号</span>
              <div className="admin-account-control"><UserRound size={17} /><input id="admin-username" className="admin-input" autoComplete="username" spellCheck={false} value={username} onChange={(event) => setUsername(event.target.value)} placeholder="输入管理员账号" maxLength={64} required disabled={signingIn} /></div>
            </label>
            <label className="admin-field" htmlFor="admin-password">
              <span className="admin-field-label">密码</span>
              <div className="admin-token-control">
                <input id="admin-password" className="admin-input" type={showPassword ? 'text' : 'password'} autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="输入密码" maxLength={128} required disabled={signingIn} />
                <button className="admin-icon-button" type="button" aria-label={showPassword ? '隐藏密码' : '显示密码'} onClick={() => setShowPassword(!showPassword)} disabled={signingIn}>{showPassword ? <EyeOff size={18} /> : <Eye size={18} />}</button>
              </div>
            </label>
            <button className="admin-button admin-button-primary" type="submit" disabled={signingIn}>
              {signingIn ? <RefreshCw className="admin-spin" size={16} /> : <ChevronRight size={17} />}{signingIn ? '登录中' : '登录'}
            </button>
          </form>
          <p className="admin-auth-note">首次登录默认账号 <strong>admin</strong>，密码 <strong>admin123</strong>。登录后可在右上角修改密码。</p>
        </section> : activePanel === 'updates' ? <DataUpdatesPanel request={adminRequest} /> : <>
          <section className="admin-summary" aria-label="密钥统计">
            {(['all', 'active', 'expired', 'revoked'] as const).map((status) => <div key={status}>
              <span>
                {status === 'all' ? '全部密钥' : STATUS_LABELS[status]}
              </span>
              <strong>
                {status === 'all' ? keys.length : keys.filter((key) => keyStatus(key) === status).length}
              </strong>
            </div>)}
          </section>
          <section className="admin-key-section" aria-labelledby="key-list-title" aria-busy={loading}>
            <div className="admin-list-heading">
              <div>
                <h2 id="key-list-title">密钥列表</h2>
                <p>查看完整密钥、复制凭证或撤销访问权限。</p>
              </div>
              <button className="admin-button admin-button-primary" type="button" onClick={startCreate}>
                <Plus size={16} />
                创建密钥
              </button>
            </div>
            <div className="admin-toolbar">
              <div className="admin-search">
                <Search size={17} />
                <input aria-label="搜索密钥" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="搜索名称或密钥前缀" />
              </div>
              <select
                className="admin-input admin-filter"
                aria-label="筛选密钥状态"
                value={filter}
                onChange={(event) => setFilter(event.target.value)}
              >
                <option value="all">全部状态</option>
                <option value="active">有效</option>
                <option value="expired">已过期</option>
                <option value="revoked">已撤销</option>
              </select>
              <button className="admin-button admin-refresh" type="button" onClick={() => void loadKeys()} disabled={loading}>
                <RefreshCw className={loading ? 'admin-spin' : ''} size={16} />
                {loading ? '更新中' : '刷新列表'}
              </button>
            </div>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th scope="col">名称 / 密钥前缀</th>
                    <th scope="col">状态</th>
                    <th scope="col">创建时间</th>
                    <th scope="col">最后使用</th>
                    <th scope="col">有效期</th>
                    <th scope="col">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleKeys.map((apiKey) => <tr key={apiKey.id}>
                    <td className="admin-key-identity">
                      <strong>
                        {apiKey.name}
                      </strong>
                      <code>
                        {apiKey.prefix}
                      </code>
                      {!apiKey.can_reveal && <span className="admin-legacy">历史密钥 · 无法查看完整值</span>}
                    </td>
                    <td data-label="状态">
                      <KeyState apiKey={apiKey} />
                    </td>
                    <td data-label="创建时间">
                      {formatDate(apiKey.created_at)}
                    </td>
                    <td data-label="最后使用">
                      {formatDate(apiKey.last_used_at)}
                    </td>
                    <td data-label="有效期">
                      {apiKey.expires_at ? formatDate(apiKey.expires_at) : <span className="admin-never">永不过期</span>}
                    </td>
                    <td className="admin-actions-cell">
                      <div className="admin-row-actions">
                        <button
                          className="admin-row-button"
                          type="button"
                          aria-label={`查看 ${apiKey.name}`}
                          disabled={!apiKey.can_reveal || !!revealing}
                          title={!apiKey.can_reveal ? '历史密钥未保存可恢复的完整值' : '查看完整密钥'}
                          onClick={() => void reveal(apiKey)}
                        >
                          <Eye size={15} />
                          查看
                        </button>
                        <button
                          className="admin-row-button"
                          type="button"
                          aria-label={`复制 ${apiKey.name}`}
                          disabled={!apiKey.can_reveal || !!revealing}
                          onClick={() => void reveal(apiKey, true)}
                        >
                          <Clipboard size={15} />
                          复制
                        </button>
                        {keyStatus(apiKey) === 'active' && <button
                          className="admin-row-button admin-revoke-button"
                          type="button"
                          aria-label={`撤销 ${apiKey.name}`}
                          onClick={() => {
                            closeModal()
                            setRevokeTarget(apiKey)
                          }}
                        >撤销</button>}
                      </div>
                    </td>
                  </tr>)}
                </tbody>
              </table>
              {!visibleKeys.length && <div className="admin-empty">
                <KeyRound size={28} />
                <h3>
                  {loading ? '正在加载密钥' : keys.length ? '没有匹配的密钥' : '暂无 API 密钥'}
                </h3>
                <p>
                  {keys.length ? '尝试其他名称、前缀或状态。' : '创建一个密钥，让应用接入股票数据接口。'}
                </p>
                {!loading && <button className="admin-button" type="button" onClick={keys.length ? () => {
                  setSearch('')
                  setFilter('all')
                } : startCreate}>
                  {keys.length ? '清空筛选' : '创建第一个密钥'}
                </button>}
              </div>}
            </div>
            <div className="admin-list-footer">
              <span>
                显示
                {visibleKeys.length}
                /
                {keys.length}
                个密钥
              </span>
              <span>完整密钥可随时查看和复制</span>
            </div>
          </section>
        </>}
      </div>
      {showChangePassword && <Modal title="修改密码" description="输入当前密码，并为管理员账户设置新密码。修改后需要重新登录。" onClose={closeModal} busy={changingPassword}>
        <form className="admin-create-form" onSubmit={changePassword}>
          <ErrorNotice message={modalError} />
          {([
            { id: 'current', label: '当前密码', value: currentPassword, set: setCurrentPassword, autoComplete: 'current-password' },
            { id: 'new', label: '新密码', value: newPassword, set: setNewPassword, autoComplete: 'new-password' },
            { id: 'confirm', label: '确认新密码', value: confirmPassword, set: setConfirmPassword, autoComplete: 'new-password' },
          ]).map((field) => <label key={field.id} className="admin-field" htmlFor={`password-${field.id}`}>
            <span className="admin-field-label">{field.label}</span>
            <div className="admin-token-control">
              <input id={`password-${field.id}`} aria-label={field.label} aria-describedby={field.id === 'new' ? 'new-password-help' : undefined} className="admin-input" type={visiblePasswords[field.id] ? 'text' : 'password'} autoComplete={field.autoComplete} value={field.value} onChange={(event) => field.set(event.target.value)} maxLength={128} required disabled={changingPassword} />
              <button className="admin-icon-button" type="button" aria-label={`${visiblePasswords[field.id] ? '隐藏' : '显示'}${field.label}`} disabled={changingPassword} onClick={() => setVisiblePasswords((current) => ({ ...current, [field.id]: !current[field.id] }))}>{visiblePasswords[field.id] ? <EyeOff size={18} /> : <Eye size={18} />}</button>
            </div>
            {field.id === 'new' && <span id="new-password-help" className="admin-field-help">使用 8–128 个字符。</span>}
          </label>)}
          <div className="admin-dialog-actions">
            <button className="admin-button" type="button" onClick={closeModal} disabled={changingPassword}>取消</button>
            <button className="admin-button admin-button-primary" type="submit" disabled={changingPassword}>{changingPassword ? <RefreshCw className="admin-spin" size={16} /> : <Check size={16} />}{changingPassword ? '保存中' : '保存新密码'}</button>
          </div>
        </form>
      </Modal>}
      {showCreate && <Modal title="创建密钥" description="为接入应用命名，并选择访问凭证的有效期。" onClose={closeModal} busy={creating}>
        <form className="admin-create-form" onSubmit={createKey}>
          <ErrorNotice message={modalError} />
          <label className="admin-field">
            <span className="admin-field-label">密钥名称</span>
            <input
              className="admin-input"
              value={name}
              onChange={(event) => setName(event.target.value)}
              maxLength={100}
              required
              placeholder="例如：行情终端"
              disabled={creating}
            />
          </label>
          <fieldset className="admin-expiry">
            <legend className="admin-field-label">有效期</legend>
            <label>
              <input
                type="radio"
                name="expiry"
                value="never"
                checked={expiryMode === 'never'}
                onChange={() => setExpiryMode('never')}
                disabled={creating}
              />
              永不过期
            </label>
            <label>
              <input
                type="radio"
                name="expiry"
                value="custom"
                checked={expiryMode === 'custom'}
                onChange={() => setExpiryMode('custom')}
                disabled={creating}
              />
              设置过期时间
            </label>
          </fieldset>
          {expiryMode === 'custom' && <label className="admin-field">
            <span className="admin-field-label">过期时间</span>
            <input
              className="admin-input"
              type="datetime-local"
              aria-label="过期时间"
              aria-describedby="admin-expiry-help"
              value={expiresAt}
              onChange={(event) => setExpiresAt(event.target.value)}
              required
              disabled={creating}
            />
            <span id="admin-expiry-help" className="admin-field-help">按当前设备的本地时间设置。</span>
          </label>}
          <div className="admin-dialog-actions">
            <button className="admin-button" type="button" onClick={closeModal} disabled={creating}>取消</button>
            <button className="admin-button admin-button-primary" type="submit" disabled={creating || !name.trim()}>
              {creating ? <RefreshCw className="admin-spin" size={16} /> : <Plus size={16} />}
              {creating ? '创建中' : '确认创建'}
            </button>
          </div>
        </form>
      </Modal>}
      {detail && <Modal title="密钥详情" description="完整密钥可在管理会话中随时查看和复制。" onClose={closeModal} busy={!!revealing}>
        <div className="admin-detail-meta">
          <strong>
            {detail.name}
          </strong>
          <KeyState apiKey={detail} />
          <span>
            有效期：
            {detail.expires_at ? formatDate(detail.expires_at) : '永不过期'}
          </span>
        </div>
        <ErrorNotice message={modalError} />
        {revealing && !secret ? <p className="admin-reveal-loading" role="status">
          <RefreshCw className="admin-spin" size={16} />
          正在读取完整密钥…
        </p> : secret ? <label className="admin-secret-label">
          <span className="admin-field-label">完整密钥</span>
          <textarea
            ref={secretInput}
            className="admin-secret-field"
            aria-label="完整密钥"
            readOnly
            value={secret}
            onFocus={(event) => event.currentTarget.select()}
            spellCheck={false}
            rows={3}
          />
        </label> : <button className="admin-button" type="button" onClick={() => void reveal(detail)}>重试读取</button>}
        {copyFeedback && <p className={`admin-copy-feedback ${manualCopy ? 'admin-copy-manual' : ''}`} role="status">
          {!manualCopy && <Check size={16} />}
          {copyFeedback}
        </p>}
        <div className="admin-dialog-actions">
          <button className="admin-button" type="button" onClick={closeModal} disabled={!!revealing}>关闭</button>
          <button
            className="admin-button admin-button-primary"
            type="button"
            onClick={() => void copyKey()}
            disabled={!secret || !!revealing}
          >
            <Clipboard size={16} />
            复制密钥
          </button>
        </div>
      </Modal>}
      {revokeTarget && <Modal title="撤销此密钥？" description={`“${revokeTarget.name}”将立即失去 API 访问权限，此操作不可恢复。`} onClose={closeModal} busy={revoking} danger>
        <ErrorNotice message={modalError} />
        <div className="admin-revoke-target">
          <KeyRound size={18} />
          <code>
            {revokeTarget.prefix}
          </code>
        </div>
        <div className="admin-dialog-actions">
          <button className="admin-button" type="button" onClick={closeModal} disabled={revoking}>取消</button>
          <button className="admin-button admin-button-danger" type="button" onClick={() => void revokeKey()} disabled={revoking}>
            {revoking ? <RefreshCw className="admin-spin" size={16} /> : null}
            {revoking ? '撤销中' : '确认撤销'}
          </button>
        </div>
      </Modal>}
    </main>
  )
}
